/**
 * PayPage â€” the core "review and pay" screen.
 *
 * One page handles BOTH flows per scoping decision (#2):
 *
 *   - Balance-pay flow:  /pay
 *     Fetches `/payment/me` only.
 *     Submits to `/payment/charge`.
 *
 *   - Precollect flow:   /checkin/:appointmentId
 *     Fetches BOTH `/payment/me` AND `/checkin/balance/:id` in parallel.
 *     `/payment/me` provides patient + clinic + Stripe wiring + the rich
 *     per-procedure open_balances. `/checkin/balance/:id` provides the
 *     appointment estimate, the credit-balance signal, and the richer
 *     earmarked_prepayments list (with appointment_date).
 *     Submits to `/checkin/payment`.
 *
 * The two flows differ only in:
 *   1. Whether the second (checkin) query is enabled
 *   2. Whether the EstimatedResponsibility section renders
 *   3. Which intent-creation endpoint receives the submit
 *
 * **v0.7.0 â€” estimate is all-or-nothing.** Per the backend handoff dated
 * 2026-05-xx, patients can pay the full remaining estimate or skip it
 * entirely â€” no partial estimate amounts. The previous typed-amount
 * input is removed; a single boolean drives whether the estimate is
 * included in the charge.
 *
 * **v0.7.0 â€” Stripe-minimum gating.** Stripe rejects USD charges under
 * $0.50. The backend now rejects these too; the UI gates client-side
 * so the patient sees a useful explanation instead of an opaque 422.
 * Three sub-states:
 *   - Estimate remaining < $0.50: estimate shown as info-only, no checkbox.
 *   - Balance selection sums to < $0.50 (no estimate): Pay button disabled
 *     with explanation that small balances must be paid at the clinic.
 *   - Combined total < $0.50: same gate at the form level.
 *
 * Empty and credit states (UI_REWRITE Â§5.2 / Â§5.3):
 *   - No outstanding balance + no estimate â†’ explicit empty-state copy.
 *   - `current_balance_minor_units < 0` â†’ render as "$X credit" alert,
 *     green, not red. As of backend v0.7.1, this field is present on
 *     BOTH `/payment/me` and `/checkin/balance` at top level; the
 *     credit render reads from whichever response is in hand.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Wallet } from 'lucide-react';

import { Alert, EmptyState, Skeleton } from '@tensaw/design-system/feedback';
import { Card, CardContent } from '@tensaw/design-system/layout';
import { Stepper } from '@tensaw/design-system/navigation';

import { ApiError } from '../../api/client';
import {
  queryKeys,
  useCheckinBalance,
  usePaymentMe,
} from '../../api/queries';
import type {
  EarmarkedPrepayment,
  FetchCheckinBalanceResponse,
  FetchPaymentMeResponse,
} from '../../api/types';
import { format, sumCents } from '../../lib/money';
import { pickContactPhone } from '../../lib/contactPhone';
import { PortalLayout } from '../../PortalLayout';
import { StripeProvider } from '../../stripe/StripeProvider';
import type { PaymentRequest } from '../../stripe/usePaymentSubmit';

import { EstimatedResponsibility, STRIPE_USD_MIN_CENTS } from './EstimatedResponsibility';
import { OutstandingAccount } from './OutstandingAccount';
import { PayPageHeader } from './PayPageHeader';
import { StripePaymentSection } from './StripePaymentSection';

const STEPS = [
  { label: 'Sign in' },
  { label: 'Review & pay' },
  { label: 'Confirmation' },
];

export function PayPage() {
  const params = useParams<{ appointmentId?: string }>();
  const appointmentIdRaw = params.appointmentId;
  const appointmentId = appointmentIdRaw ? Number(appointmentIdRaw) : null;
  const isPrecollect = appointmentId !== null && Number.isFinite(appointmentId);

  // v0.8.4 / backend v0.8.2: /payment/me is forbidden for appointment-scope
  // sessions (would 401 with APPOINTMENT_SCOPE_VIOLATION per design Â§6.1).
  // Disable the query in precollect; all the shared identity / stripe /
  // open_balances fields now come from /checkin/balance which carries
  // identical wire shapes (backend reuses the Pydantic classes).
  const meQuery = usePaymentMe({ enabled: !isPrecollect });
  // /checkin/balance/:id only runs in precollect mode.
  const checkinQuery = useCheckinBalance(isPrecollect ? appointmentId : null);

  const isLoading = isPrecollect
    ? checkinQuery.isLoading
    : meQuery.isLoading;
  const error = isPrecollect ? checkinQuery.error : meQuery.error;

  const ready = isPrecollect ? !!checkinQuery.data : !!meQuery.data;
  if (isLoading || !ready) {
    return (
      <PortalLayout>
        <Stepper currentStep={2} steps={STEPS} className="mb-6" />
        <PayPageSkeleton error={error} />
      </PortalLayout>
    );
  }

  // Layout-level clinic data: prefer checkin (precollect flow), fall back
  // to me. One of the two is always non-null at this point per `ready`.
  const layoutClinic = checkinQuery.data?.clinic ?? meQuery.data?.clinic ?? null;

  return (
    <PortalLayout
      clinicName={layoutClinic?.name ?? undefined}
      // Footer "call us" link is patient-in-billing-context; prefer
      // billing_phone with fallback to clinic phone.
      clinicPhone={
        layoutClinic ? pickContactPhone(layoutClinic) ?? undefined : undefined
      }
    >
      <Stepper currentStep={2} steps={STEPS} className="mb-6" />
      <PayPageBody
        me={meQuery.data ?? null}
        checkin={isPrecollect ? checkinQuery.data ?? null : null}
        appointmentId={isPrecollect ? appointmentId : null}
      />
    </PortalLayout>
  );
}

function PayPageBody({
  me,
  checkin,
  appointmentId,
}: {
  me: FetchPaymentMeResponse | null;
  checkin: FetchCheckinBalanceResponse | null;
  appointmentId: number | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // v0.8.4: derive a `view` object for fields that live in BOTH responses
  // with identical shapes (per backend v0.8.2). The precollect flow has
  // `checkin` non-null and `me` null; the balance-pay flow has `me`
  // non-null and `checkin` null. The exactly-one-non-null invariant is
  // enforced by `PayPage` above.
  //
  // Backend reuses the same Pydantic class for these on both endpoints,
  // so the TypeScript types match field-for-field. One source of truth
  // for identity / stripe / open balances regardless of flow.
  const view = checkin ?? me;
  if (!view) {
    // Defensive: PayPage's `ready` gate above prevents this. Render
    // nothing rather than crash if invariants ever break.
    return null;
  }

  // Read optimistic state (instantly hides paid items before asynchronous webhook posts)
  const [justPaidVisits, setJustPaidVisits] = useState<Set<number>>(() => {
    try {
      const saved = sessionStorage.getItem('justPaidVisits');
      return saved ? new Set(JSON.parse(saved).map(Number)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [justPaidEstimate, setJustPaidEstimate] = useState<number>(() => {
    const saved = sessionStorage.getItem('justPaidEstimate');
    return saved ? parseInt(saved, 10) : 0;
  });

  // Clear optimistic state when patient changes
  useEffect(() => {
    const savedPatientId = sessionStorage.getItem('justPaidPatientId');
    if (savedPatientId && savedPatientId !== view.patient.id.toString()) {
      sessionStorage.removeItem('justPaidVisits');
      sessionStorage.removeItem('justPaidEstimate');
      sessionStorage.removeItem('justPaidPatientId');
      setJustPaidVisits(new Set());
      setJustPaidEstimate(0);
    }
  }, [view.patient.id]);

  // Self-heal: clear optimistic state once the backend webhook has updated the server state
  useEffect(() => {
    if (justPaidVisits.size > 0) {
      const serverVisitIds = new Set(view.open_balances.map((b) => b.visit_id));
      const anyStillPending = Array.from(justPaidVisits).some((id) => serverVisitIds.has(id));
      if (!anyStillPending) {
        sessionStorage.removeItem('justPaidVisits');
        setJustPaidVisits(new Set());
      }
    }
  }, [view.open_balances, justPaidVisits]);

  useEffect(() => {
    if (justPaidEstimate > 0 && checkin && checkin.estimate_remaining_minor_units === 0) {
      sessionStorage.removeItem('justPaidEstimate');
      setJustPaidEstimate(0);
    }
  }, [checkin, justPaidEstimate]);

  const optimisticOpenBalances = useMemo(() => {
    return view.open_balances.filter((b) => !justPaidVisits.has(b.visit_id));
  }, [view.open_balances, justPaidVisits]);

  // Selection: which past visits is the patient paying, and how much
  // (if any) toward the estimate. Defaults: all visits selected, full
  // remaining estimate selected.
  const [selectedVisitIds, setSelectedVisitIds] = useState<Set<number>>(
    () => new Set(optimisticOpenBalances.map((b) => b.visit_id)),
  );
  useEffect(() => {
    setSelectedVisitIds(new Set(optimisticOpenBalances.map((b) => b.visit_id)));
  }, [optimisticOpenBalances]);

  // Per Patient-Payment-Portal-Backend-v0.7.0 handoff: estimate is
  // all-or-nothing. The checkbox pre-checks ONLY when remaining is
  // chargeable (â‰¥ Stripe minimum $0.50); below that, it's display-only
  // and selected is forced false (see EstimatedResponsibility).
  const remainingEstimate = Math.max(0, (checkin?.estimate_remaining_minor_units ?? 0) - justPaidEstimate);
  const estimateChargeable = remainingEstimate >= STRIPE_USD_MIN_CENTS;
  const [estimateSelected, setEstimateSelected] = useState<boolean>(
    () => estimateChargeable,
  );

  const visitTotal = sumCents(
    optimisticOpenBalances
      .filter((b) => selectedVisitIds.has(b.visit_id))
      .map((b) => b.patient_balance),
  );
  const estimateTotal =
    checkin && estimateSelected && estimateChargeable ? remainingEstimate : 0;
  const grandTotal = visitTotal + estimateTotal;

  // Sub-Stripe-minimum gating for the balance-pay flow. If the only
  // selected visits sum to 1..49 cents, Stripe will reject. Mirror the
  // estimate logic and disable Pay with an explanatory banner. Mixed
  // cases (one $0.30 + one $50) handle themselves â€” patient deselects
  // the $0.30 to bring the total above the floor.
  const balanceSubMinimum =
    visitTotal > 0 && visitTotal < STRIPE_USD_MIN_CENTS;
  // The combined total can also fall below the floor (e.g. unchecked
  // estimate + tiny balance). Same gate.
  const grandSubMinimum =
    grandTotal > 0 && grandTotal < STRIPE_USD_MIN_CENTS;

  const stripeUnavailable = view.stripe.connected_account_id === null;
  const nothingToPay =
    optimisticOpenBalances.length === 0 &&
    (checkin === null || remainingEstimate === 0);
  // Credit is the negation of a negative current_balance_minor_units.
  // Both endpoints carry the field at the top level as of backend v0.7.1;
  // `view` gives us the right source automatically (checkin in precollect,
  // me in balance-pay).
  const paidVisitsAmount = sumCents(
    view.open_balances
      .filter((b) => justPaidVisits.has(b.visit_id))
      .map((b) => b.patient_balance)
  );
  const optimisticCurrentBalance = (view.current_balance_minor_units ?? 0) - paidVisitsAmount;
  const rawBalance = optimisticCurrentBalance;
  const creditCents = rawBalance < 0 ? -rawBalance : 0;
  //
  // v0.8.5: CreditAlert hidden by product decision. The copy ("we'll
  // automatically apply your $X credit to your next visit") was
  // confusing â€” particularly in precollect context, where "your next
  // visit" IS the current screen. Product chose to drop the disclosure
  // entirely rather than rework copy.
  //
  // The credit-detection logic above is preserved so the field still
  // resolves correctly (other code may key off it later). The constant
  // below gates both render sites â€” flip to `true` to restore the alert
  // without other code changes.
  //
  // Patient impact: patients with credit on file no longer see an
  // explicit disclosure on this screen. The credit still applies
  // server-side to subsequent charges per backend's existing logic;
  // there is no FE behavioral change beyond the missing notice.
  //
  const SHOW_CREDIT_ALERT = false;

  const buildRequest = (args: {
    email: string;
    updateEmailOnRecord: boolean;
  }): PaymentRequest | null => {
    if (grandTotal <= 0) return null;
    // v0.8.6: email and update_email_on_record are required on both
    // charge endpoints per backend v0.8.5. The StripePaymentSection
    // owns the form state and passes the values in via this arg
    // object. Email shape validation happens FE-side before this
    // function is called; backend re-validates and 422s if absent.
    if (checkin && appointmentId !== null) {
      // Precollect submit. Per Patient-Payment-Portal-Backend-v0.7.0:
      //   - estimate_amount_minor_units MUST be either 0 (skip) or
      //     exactly estimate_remaining_minor_units (pay in full). The
      //     UI gates this â€” `estimateTotal` is computed from a single
      //     boolean, never a typed-in partial value.
      //   - balance_amount_minor_units MUST equal the sum of patient_balance
      //     across visit_ids exactly (no partial)
      //   - if visit_ids is empty, balance_amount_minor_units must be 0
      //   - Combined total must be >= Stripe minimum ($0.50)
      return {
        kind: 'precollect',
        request: {
          appointment_id: appointmentId,
          estimate_amount_minor_units: estimateTotal,
          balance_amount_minor_units: visitTotal,
          visit_ids: Array.from(selectedVisitIds),
          currency: 'usd',
          email: args.email,
          update_email_on_record: args.updateEmailOnRecord,
        },
      };
    }
    return {
      kind: 'balance',
      request: {
        amount_minor_units: visitTotal,
        currency: 'usd',
        visit_ids: Array.from(selectedVisitIds),
        procedure_ids: null,
        email: args.email,
        update_email_on_record: args.updateEmailOnRecord,
      },
    };
  };

  const handleSuccess = ({
    paymentSessionId,
    amountCents,
  }: {
    paymentSessionId: string;
    paymentIntentId: string;
    amountCents: number;
  }) => {
    // Save optimistic state to sessionStorage so we don't display paid items
    // while the asynchronous backend webhook is processing the ledger update.
    sessionStorage.setItem('justPaidVisits', JSON.stringify(Array.from(selectedVisitIds)));
    if (checkin && estimateSelected && estimateChargeable) {
      sessionStorage.setItem('justPaidEstimate', remainingEstimate.toString());
    }
    sessionStorage.setItem('justPaidPatientId', view.patient.id.toString());

    // Invalidate queries so that subsequent mounts of PayPage or checkin view fetch the latest balance.
    if (appointmentId !== null) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checkinBalance(appointmentId),
      });
    }
    void queryClient.invalidateQueries({
      queryKey: queryKeys.paymentMe(),
    });

    navigate('/success', {
      state: {
        paymentSessionId,
        amountCents,
        clinicName: view.clinic.name,
        // SuccessPage is a post-payment "call us" context â€” billing.
        // Use the same fallback helper as the layout footer so both
        // hold the same number across navigation.
        clinicPhone: pickContactPhone(view.clinic),
        patientName: view.patient.name,
        // Pass appointmentId so SuccessPage's "Back to my account" can
        // route the patient back to /checkin/<id> rather than /pay. Per
        // QA BUG-013, hard-coding /pay here is wrong for precollect
        // arrivals â€” they want to land back on the checkin context.
        appointmentId,
      },
    });
  };

  // Per UI_REWRITE Â§5.5: when the precollect submit returns AMOUNT_MISMATCH,
  // refresh the estimate from /checkin/balance and ask the patient to review
  // again. We pass a callback the StripePaymentSection can invoke; it
  // invalidates the query so React Query refetches.
  const handleAmountMismatch = () => {
    if (appointmentId !== null) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.checkinBalance(appointmentId),
      });
    } else {
      void queryClient.invalidateQueries({ queryKey: queryKeys.paymentMe() });
    }
  };

  // Earmarked-prepayments: the checkin response carries the rich variant
  // (with appointment_date and type), the /payment/me response carries
  // the simple variant. Source per flow.
  const earmarkedRich = checkin?.earmarked_prepayments ?? null;
  const earmarkedSimple: EarmarkedPrepayment[] = me?.earmarked_prepayments ?? [];

  if (nothingToPay) {
    return (
      <div className="grid grid-cols-1 gap-6">
        <PayPageHeader clinic={view.clinic} patient={view.patient} />
        <Card>
          <CardContent className="p-8">
            <EmptyState
              size="md"
              icon={<CheckCircle2 className="h-10 w-10 text-green-600" />}
              title="You have no outstanding balance"
              description={
                checkin
                  ? "There's nothing to pay right now your estimate is fully paid and your account is current."
                  : "There's nothing to pay right now. Your account is current."
              }
            />
          </CardContent>
        </Card>
        {SHOW_CREDIT_ALERT && creditCents > 0 && <CreditAlert cents={creditCents} />}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
      {/* Left column â€” clinic header, optional credit, sections, totals */}
      <div className="flex flex-col gap-6">
        <PayPageHeader clinic={view.clinic} patient={view.patient} />

        {SHOW_CREDIT_ALERT && creditCents > 0 && <CreditAlert cents={creditCents} />}

        {checkin && checkin.estimate_remaining_minor_units > 0 && (
          <EstimatedResponsibility
            data={checkin}
            selected={estimateSelected}
            onSelectedChange={setEstimateSelected}
          />
        )}

        <OutstandingAccount
          openBalances={optimisticOpenBalances}
          selectedVisitIds={selectedVisitIds}
          onSelectionChange={setSelectedVisitIds}
        />

        {earmarkedRich !== null && earmarkedRich.length > 0 ? (
          <EarmarkedPrepaymentsCardRich items={earmarkedRich} />
        ) : earmarkedSimple.length > 0 ? (
          <EarmarkedPrepaymentsCardSimple items={earmarkedSimple} />
        ) : null}
      </div>

      {/* Right column â€” Stripe payment form (sticky on desktop) */}
      <div className="lg:sticky lg:top-20 lg:self-start">
        {stripeUnavailable ? (
          <Card>
            <CardContent className="p-6">
              <Alert variant="warning" title="Online payment not yet available">
                Payments aren't enabled for this clinic yet. Please call to
                pay over the phone.
              </Alert>
            </CardContent>
          </Card>
        ) : (
          <StripeProvider
            publishableKey={view.stripe.publishable_key}
            connectedAccountId={view.stripe.connected_account_id as string}
            amountCents={Math.max(grandTotal, STRIPE_USD_MIN_CENTS)}
          >
            <StripePaymentSection
              amountCents={grandTotal}
              amountDisabled={grandTotal <= 0 || grandSubMinimum}
              subMinimumNotice={
                balanceSubMinimum && !estimateSelected
                  ? 'Balances under $0.50 must be paid at the clinic. Please bring exact change or speak to the front desk.'
                  : grandSubMinimum
                    ? `Total under ${format(STRIPE_USD_MIN_CENTS)} can't be charged online. Add another visit or pay at the clinic.`
                    : null
              }
              initialEmail={view.patient.email}
              buildRequest={buildRequest}
              onSuccess={handleSuccess}
              onAmountMismatch={handleAmountMismatch}
              clinicPhone={pickContactPhone(view.clinic)}
            />
          </StripeProvider>
        )}
      </div>
    </div>
  );
}

/**
 * Credit banner. Per UI_REWRITE Â§5.3: when the patient has loose credit,
 * render it as a positive (green) signal â€” they have money on file, not
 * a negative balance.
 */
function CreditAlert({ cents }: { cents: number }) {
  return (
    <Alert
      variant="success"
      icon={<Wallet className="h-4 w-4" aria-hidden="true" />}
      title="You have a credit on your account"
    >
      We'll automatically apply your{' '}
      <span className="font-semibold">{format(cents)}</span> credit to your
      next visit.
    </Alert>
  );
}

/** Rich variant â€” has appointment_date, used when sourced from /checkin/balance. */
function EarmarkedPrepaymentsCardRich({
  items,
}: {
  items: NonNullable<FetchCheckinBalanceResponse['earmarked_prepayments']>;
}) {
  const total = sumCents(items.map((p) => p.amount_minor_units));
  const futureItems = items.filter((p) => {
    if (!p.appointment_date) return true;

    const appointmentDate = new Date(p.appointment_date);
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    appointmentDate.setHours(0, 0, 0, 0);

    return appointmentDate > today;
  });
  return (
    <Card variant="subtle">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              {/*
                v0.8.6 B3: "Earmarked" was billing jargon leaking
                from the API field name `earmarked_prepayments`.
                Patient-facing relabel; wire field name unchanged.
              */}
              Already paid for upcoming visits
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Set aside for future appointments not part of today's
              total.
            </p>
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
              {futureItems.map((p) => (
                <li
                  key={p.appointment_id}
                  className="flex items-center justify-between gap-3"
                >
                  <span>
                    {p.appointment_date ? formatShortDate(p.appointment_date) : 'Upcoming visit'}
                  </span>
                  <span className="tabular-nums text-foreground">
                    {format(p.amount_minor_units)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Total prepaid
            </div>
            <div className="text-base font-semibold text-foreground tabular-nums">
              {format(total)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Simple variant â€” only appointment_id + amount, sourced from /payment/me. */
function EarmarkedPrepaymentsCardSimple({
  items,
}: {
  items: EarmarkedPrepayment[];
}) {
  const total = sumCents(items.map((p) => p.amount_minor_units));
  return (
    <Card variant="subtle">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Already paid for upcoming visits
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {items.length} prepayment{items.length === 1 ? '' : 's'}{' '}
              set aside for future appointments not part of today's
              total.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Total prepaid
            </div>
            <div className="text-base font-semibold text-foreground tabular-nums">
              {format(total)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PayPageSkeleton({ error }: { error: unknown }) {
  if (error) {
    return (
      <Card>
        <CardContent className="p-8">
          <Alert variant="error" title={errorTitle(error)}>
            {errorMessage(error)}
          </Alert>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
      <div className="flex flex-col gap-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
      <Skeleton className="h-96 w-full" />
    </div>
  );
}

function errorTitle(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.problem.error_code === 'PATIENT_PORTAL_APPOINTMENT_NOT_FOUND') {
      // UI_DATA_LAYER Â§3b: lump all 404 cases under one message â€” patient
      // can't act on the discriminator and revealing it could leak whether
      // an appointment exists.
      return 'This link is no longer valid';
    }
  }
  return "We couldn't load your statement";
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.problem.error_code === 'PATIENT_PORTAL_APPOINTMENT_NOT_FOUND') {
      return 'Please contact the clinic for an updated link.';
    }
    return e.problem.detail || 'Please try again, or call your clinic.';
  }
  return 'Please refresh the page or contact your clinic for help.';
}

function formatShortDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(d);
  } catch {
    return iso;
  }
}
